/** The backup facts the native stop of a stand-down reported. */
export interface StandDownStopFacts {
  finishSucceeded: boolean;
  backupUploaded: boolean;
  backupPendingAsync: boolean;
  backupDeferred: boolean;
  backupRetained: boolean;
}

/** How one start ended. */
export type RecordingStartOutcome =
  /** It left nothing running that a stop must take down. */
  | { kind: 'none' }
  /** It published this native recording and owns its capture. */
  | { kind: 'live'; recordingId: string }
  /**
   * It was fenced and stopped what it started. `stop` is undefined when no upload
   * session was stopped; `stillStopping` says the native stop outlived its wait and still owns the WAVs.
   */
  | { kind: 'stoodDown'; stop: StandDownStopFacts | undefined; stillStopping: boolean };

/**
 * The settings the fencing stop sends Rust. A stand-down's session stop sends them
 * too, since Rust keeps the first stop of a recording and replays it to later ones.
 */
export interface StandDownStopSettings {
  detachedBackupMux: boolean;
  /** Same shape as the stop IPC's offline handover; undefined hands nothing over. */
  offlineHandover:
    | {
        templateId?: string;
        dictationType?: string;
        language?: string;
        outputLanguage?: string;
        noteStyle?: string;
      }
    | undefined;
}

/** What the stop that fenced a start asks of it. */
export interface RecordingStartFence {
  /** The stop is a WAV-writer failure, so the WAV must not be read. */
  quarantineWav: boolean;
  /** The stop's reason, which a stand-down's own session stop carries. */
  reason: 'userStop' | 'pause';
  /** Undefined when the stop sends Rust no settings beyond its reason. */
  stopSettings?: StandDownStopSettings;
}

/** A device-switch restart, or a start the user asked for. */
export type RecordingStartKind = 'restart' | 'user';

/** One start invocation, held by the start mutation for its whole run. */
export interface RecordingStartTicket {
  readonly kind: RecordingStartKind;
  /** The stop fence inherited at start or added while this start is starting, if any. */
  fence: () => RecordingStartFence | undefined;
  fenceSource: () =>
    | { source: 'retained'; ageMs: number }
    | { source: 'inFlight' }
    | undefined;
  /**
   * This start holds nothing a stop or a later start must wait for, and it checks
   * `fence()` before it starts anything. From now on a fence settles it as
   * `{ kind: 'none' }` at once.
   */
  markNothingToStop: () => void;
  /** Drops a retained fence only after its publishing stop cleared it. */
  releaseRetainedFence: () => boolean;
  /** Reports how the start ended. The first call wins; later calls are ignored. */
  settle: (outcome: RecordingStartOutcome) => void;
}

interface InFlightStart {
  sessionId: string;
  kind: RecordingStartKind;
  fence: RecordingStartFence | undefined;
  fenceSource: { source: 'retained'; ageMs: number } | { source: 'inFlight' } | undefined;
  fencedInFlight: boolean;
  nothingToStop: boolean;
  settled: Promise<RecordingStartOutcome>;
  settle: (outcome: RecordingStartOutcome) => void;
}

interface RetainedRecordingStopFence {
  fence: RecordingStartFence;
  atMs: number;
  settled: Promise<void>;
  resolve: (() => void) | undefined;
}

const inFlight = new Set<InFlightStart>();
// Kept after a stop so late device-change restarts stand down; a user start clears it.
const stopFencesBySession = new Map<string, RetainedRecordingStopFence>();
const fencingStopsBySession = new Map<string, Set<Promise<void>>>();
const liveRecordingIdsBySession = new Map<string, Set<string>>();

/** Registers one device-switch restart. Call it before the restart's first await. */
export function beginMidRecordingRestart(sessionId: string): RecordingStartTicket {
  return beginStart(sessionId, 'restart');
}

/** Registers a user-initiated start before its first await and clears a prior stop fence. */
export function beginUserRecordingStart(sessionId: string): RecordingStartTicket {
  return beginStart(sessionId, 'user');
}

function beginStart(sessionId: string, kind: RecordingStartKind): RecordingStartTicket {
  if (kind === 'user') clearRecordingStopFence(sessionId);

  const retainedFence =
    kind === 'restart' ? stopFencesBySession.get(sessionId) : undefined;
  const startedAtMs = retainedFence ? Date.now() : undefined;
  let resolve: (outcome: RecordingStartOutcome) => void = () => {};
  const start: InFlightStart = {
    sessionId,
    kind,
    fence: retainedFence?.fence,
    fenceSource: retainedFence
      ? { source: 'retained', ageMs: startedAtMs! - retainedFence.atMs }
      : undefined,
    fencedInFlight: false,
    nothingToStop: false,
    settled: new Promise((r) => {
      resolve = r;
    }),
    settle: () => {},
  };
  inFlight.add(start);
  let done = false;
  const settle = (outcome: RecordingStartOutcome) => {
    if (done) return;
    done = true;
    inFlight.delete(start);
    // Only a restart's recording is a reconcile's to stop.
    if (outcome.kind === 'live' && kind === 'restart') {
      const ids = liveRecordingIdsBySession.get(sessionId) ?? new Set<string>();
      ids.add(outcome.recordingId);
      liveRecordingIdsBySession.set(sessionId, ids);
    }
    resolve(outcome);
  };
  start.settle = settle;
  return {
    kind,
    fence: () => start.fence,
    fenceSource: () => start.fenceSource,
    markNothingToStop: () => {
      if (start.fence) {
        settle({ kind: 'none' });
        return;
      }
      start.nothingToStop = true;
    },
    releaseRetainedFence: () => {
      if (
        start.fenceSource?.source !== 'retained' ||
        !start.fence ||
        start.fencedInFlight ||
        hasRecordingStopFence(sessionId)
      ) {
        return false;
      }
      start.fence = undefined;
      return true;
    },
    settle,
  };
}

function mergeFence(
  previous: RecordingStartFence | undefined,
  next: RecordingStartFence
): RecordingStartFence {
  const winner =
    previous?.reason === 'userStop' && next.reason === 'pause' ? previous : next;
  return {
    quarantineWav: next.quarantineWav || previous?.quarantineWav === true,
    reason: winner.reason,
    stopSettings: winner.stopSettings ?? previous?.stopSettings,
  };
}

/**
 * Fences in-flight restarts and carries the fence to later restarts until a user start.
 * Returns one promise per fenced restart.
 */
export function fenceMidRecordingRestarts(
  sessionId: string,
  fence: RecordingStartFence,
  opts?: { retain?: boolean }
): Promise<RecordingStartOutcome>[] {
  return fenceStarts(sessionId, fence, 'restart', opts);
}

/**
 * Fences every start of the session still starting, user starts and restarts
 * alike, so each stands down. The fence also applies to later restarts until a
 * user start claims the session. Returns one promise per fenced start.
 */
export function fenceRecordingStarts(
  sessionId: string,
  fence: RecordingStartFence,
  opts?: { retain?: boolean }
): Promise<RecordingStartOutcome>[] {
  return fenceStarts(sessionId, fence, undefined, opts);
}

function fenceStarts(
  sessionId: string,
  fence: RecordingStartFence,
  onlyKind?: RecordingStartKind,
  opts?: { retain?: boolean }
): Promise<RecordingStartOutcome>[] {
  if (opts?.retain !== false) {
    const previous = stopFencesBySession.get(sessionId);
    const mergedFence = mergeFence(previous?.fence, fence);
    if (previous?.resolve) {
      previous.fence = mergedFence;
      previous.atMs = Date.now();
    } else {
      let resolve!: () => void;
      const settled = new Promise<void>((r) => {
        resolve = r;
      });
      stopFencesBySession.set(sessionId, {
        fence: mergedFence,
        atMs: Date.now(),
        settled,
        resolve,
      });
    }
  }
  const pending: Promise<RecordingStartOutcome>[] = [];
  for (const start of inFlight) {
    if (start.sessionId !== sessionId) continue;
    if (onlyKind && start.kind !== onlyKind) continue;
    start.fence = mergeFence(start.fence, fence);
    start.fenceSource ??= { source: 'inFlight' };
    start.fencedInFlight = true;
    pending.push(start.settled);
    if (start.nothingToStop) start.settle({ kind: 'none' });
  }
  return pending;
}

/** Resolves the publishing stop's wait without removing its retained fence. */
export function settleRecordingStopFence(sessionId: string): void {
  const retained = stopFencesBySession.get(sessionId);
  if (!retained?.resolve) return;
  const resolve = retained.resolve;
  retained.resolve = undefined;
  resolve();
}

/** Waits for the retained fence's stop and reports whether the fence remains. */
export async function whenRecordingStopFenceSettles(sessionId: string): Promise<boolean> {
  const retained = stopFencesBySession.get(sessionId);
  if (!retained) return false;
  await retained.settled;
  return stopFencesBySession.has(sessionId);
}

/** Deletes the retained fence without changing fences already held by starts. */
export function clearRecordingStopFence(sessionId: string): void {
  const retained = stopFencesBySession.get(sessionId);
  if (retained?.resolve) {
    const resolve = retained.resolve;
    retained.resolve = undefined;
    resolve();
  }
  stopFencesBySession.delete(sessionId);
}

/** Whether a retained stop fence still applies to later restarts. */
export function hasRecordingStopFence(sessionId: string): boolean {
  return stopFencesBySession.has(sessionId);
}

/** The kinds of the session's starts still in flight, for a stop to log what it fences. */
export function inFlightRecordingStartKinds(sessionId: string): RecordingStartKind[] {
  return [...inFlight]
    .filter((start) => start.sessionId === sessionId)
    .map((start) => start.kind);
}

/**
 * Held by a stop that fenced a start, until its own session stop is done. Its
 * retries carry no epoch, so a start that claimed one meanwhile would be stopped.
 * Returns the release; later calls are ignored.
 */
export function holdRecordingStartsForStop(sessionId: string): () => void {
  let release: () => void = () => {};
  const held = new Promise<void>((r) => {
    release = r;
  });
  const holds = fencingStopsBySession.get(sessionId) ?? new Set<Promise<void>>();
  holds.add(held);
  fencingStopsBySession.set(sessionId, holds);
  return () => {
    release();
    holds.delete(held);
    if (holds.size === 0 && fencingStopsBySession.get(sessionId) === holds) {
      fencingStopsBySession.delete(sessionId);
    }
  };
}

/**
 * Settles once every fenced start of the session has and every stop that fenced
 * one is done; undefined when there is none, or when an unfenced start of the
 * session is still in flight and owns it. A new start waits on it rather than
 * being dropped behind a start that stands down.
 */
export function whenFencedStartsSettle(sessionId: string): Promise<void> | undefined {
  const starts = [...inFlight].filter((start) => start.sessionId === sessionId);
  if (starts.some((start) => !start.fence)) return undefined;
  const waits = [
    ...starts.map((start) => start.settled),
    ...(fencingStopsBySession.get(sessionId) ?? []),
  ];
  if (waits.length === 0) return undefined;
  return Promise.all(waits).then(() => undefined);
}

/** Whether a restart of the session published this native recording, or any when none is named. */
export function isMidRecordingRestartRecording(
  sessionId: string,
  recordingId?: string
): boolean {
  const ids = liveRecordingIdsBySession.get(sessionId);
  if (!ids) return false;
  return recordingId === undefined ? ids.size > 0 : ids.has(recordingId);
}

/**
 * The session's restart recordings are no longer a stop's to take: a user start
 * has claimed the session, or a stop already dealt with them. Read at stop time,
 * so it also disarms a stop still in flight.
 */
export function forgetMidRecordingRestarts(sessionId: string): void {
  liveRecordingIdsBySession.delete(sessionId);
}

/** Test-only: reset module state between cases. */
export function __resetMidRecordingRestarts(): void {
  inFlight.clear();
  for (const retained of stopFencesBySession.values()) {
    if (!retained.resolve) continue;
    const resolve = retained.resolve;
    retained.resolve = undefined;
    resolve();
  }
  stopFencesBySession.clear();
  liveRecordingIdsBySession.clear();
  fencingStopsBySession.clear();
}
